// IAM / admin API client. Talks to the FastAPI /api/iam surface; attaches the
// Supabase (or dev) bearer token set by the AuthContext.
import axios from 'axios';
import { resolveApiBaseUrl } from './resolveApiBaseUrl';
import { attachAuth, setAuthToken } from './authToken';
// #168 WS2: billing 402/403 envelopes render app-wide (mapped onto .detail).
// #970: STATIC on purpose — keep it that way. The old
// `import('./billingApi').then(...)` installed the interceptor on a microtask,
// after this module finished evaluating, leaving a window in which a request
// went out unmapped. billingApi.js reaches only axios, ./resolveApiBaseUrl and
// ./authToken, so no cycle needs breaking, and iam/billing.jsx imports it
// statically already, so nothing could be code-split out either.
import { installBillingEnvelopeInterceptor } from './billingApi';

const base = resolveApiBaseUrl(); // already ends with /api
const client = axios.create({ baseURL: `${base}/iam` });

// Re-exported for back-compat: AuthContext imports setAuthToken from here.
export { setAuthToken };
client.interceptors.request.use(attachAuth);
installBillingEnvelopeInterceptor(client);

const d = (p) => p.then((r) => r.data);

export const iamApi = {
  // me / onboarding
  me: () => d(client.get('/me')),
  myOrgs: () => d(client.get('/orgs')),
  createOrg: (name) => d(client.post('/orgs', { name })),
  renameOrg: (oid, name) => d(client.put(`/orgs/${oid}`, { name })),
  deleteOrg: (oid) => d(client.delete(`/orgs/${oid}`)),
  acceptInvite: (token) => d(client.post('/invitations/accept', { token })),

  // org admin
  orgMembers: (oid) => d(client.get(`/orgs/${oid}/members`)),
  invite: (oid, body) => d(client.post(`/orgs/${oid}/invitations`, body)),
  orgInvitations: (oid) => d(client.get(`/orgs/${oid}/invitations`)),
  assignOrgRole: (oid, uid, role) => d(client.put(`/orgs/${oid}/members/${uid}/role`, { role })),
  removeOrgMember: (oid, uid) => d(client.delete(`/orgs/${oid}/members/${uid}`)),
  resetMemberPassword: (oid, uid) => d(client.post(`/orgs/${oid}/members/${uid}/reset-password`)),
  memberMagicLink: (oid, uid) => d(client.post(`/orgs/${oid}/members/${uid}/magic-link`)),
  // #212: security baseline
  revokeMemberSessions: (oid, uid) => d(client.post(`/orgs/${oid}/members/${uid}/revoke-sessions`)),
  exportOrg: (oid) => d(client.get(`/orgs/${oid}/export`)),
  // N-11 / REQ-6130 (#601): scoped, ADDITIVE role grants — the admin surface for the
  // `approver` role #591 made a distinct grant. These sit alongside the single
  // membership role (`assignOrgRole` above), which is why they are separate calls
  // rather than another value in that Select: a member keeps `editor` AND holds
  // `approver`, org-wide (`project_id` omitted) or within one project.
  /**
   * @param {string} oid
   * @param {{ userId?: string, projectId?: string }} [filter]
   */
  roleGrants: (oid, { userId, projectId } = {}) => d(client.get(`/orgs/${oid}/role-grants`, {
    params: { ...(userId ? { user_id: userId } : {}), ...(projectId ? { project_id: projectId } : {}) },
  })),
  grantRole: (oid, { userId, role, projectId = null }) => d(client.post(`/orgs/${oid}/role-grants`, {
    user_id: userId, role, ...(projectId ? { project_id: projectId } : {}),
  })),
  revokeRoleGrant: (oid, grantId) => d(client.delete(`/orgs/${oid}/role-grants/${grantId}`)),
  adminRevokeSessions: (uid) => d(client.post(`/admin/users/${uid}/revoke-sessions`)),
  adminEraseUser: (uid) => d(client.post(`/admin/users/${uid}/erase`)),

  // projects
  projects: (oid) => d(client.get(`/orgs/${oid}/projects`)),
  // Projects the caller may VIEW in an org (admin: all; member: their memberships) —
  // source for the requirements tree's per-user 'visible projects' picker.
  myProjects: (oid) => d(client.get('/me/projects', { params: { org_id: oid } })),
  createProject: (oid, name) => d(client.post(`/orgs/${oid}/projects`, { name })),
  getProject: (pid) => d(client.get(`/projects/${pid}`)),
  renameProject: (pid, name) => d(client.put(`/projects/${pid}`, { name })),
  archiveProject: (pid) => d(client.delete(`/projects/${pid}`)),
  // Super-admin-only, irreversible hard delete (#195) — distinct from archiveProject above.
  hardDeleteProject: (pid) => d(client.delete(`/projects/${pid}/hard-delete`)),
  // Copy a project (#195 part 2): mode is 'empty' (shell + item-types only) or
  // 'full' (also duplicates every requirement, preserving hierarchy + links).
  copyProject: (pid, { name, mode = 'empty' }) => d(client.post(`/projects/${pid}/copy`, { name, mode })),
  // Export a WHOLE project (#287) as one Markdown document — every top-level item's
  // subtree, concatenated server-side. Unwraps to `{ id, format, content }`, matching
  // requirementsApi.exportMarkdown's per-item shape.
  exportProjectMarkdown: (pid) => d(client.get(`/projects/${pid}/export/markdown`)).then((r) => r.data),
  // #381: project-level ReqIF (server-rendered) + structured JSON (the dashboard
  // renders Excel/CSV + PDF/print from the JSON client-side, reusing the per-item
  // Document-tab exporters).
  exportProjectReqif: (pid) => d(client.get(`/projects/${pid}/export/reqif`)).then((r) => r.data),
  exportProjectJson: (pid) => d(client.get(`/projects/${pid}/export/json`)).then((r) => r.data),
  // #381 follow-up: Word (.docx) + Excel (.xlsx) — real binary downloads
  // (responseType 'blob'; the server streams the bytes, no base64+JSON envelope
  // that once froze the worker on a large project). Resolve to the Blob itself.
  exportProjectDocx: (pid) => client.get(`/projects/${pid}/export/docx`, { responseType: 'blob' }).then((r) => r.data),
  exportProjectXlsx: (pid) => client.get(`/projects/${pid}/export/xlsx`, { responseType: 'blob' }).then((r) => r.data),
  projectMembers: (pid) => d(client.get(`/projects/${pid}/members`)),
  assignProjectRole: (pid, uid, role) => d(client.put(`/projects/${pid}/members/${uid}/role`, { role })),
  removeProjectMember: (pid, uid) => d(client.delete(`/projects/${pid}/members/${uid}`)),

  // item-type meta-model (#196)
  projectItemTypes: (pid) => d(client.get(`/projects/${pid}/item-types`)),
  orgItemTypes: (oid) => d(client.get(`/orgs/${oid}/item-types`)),
  createItemType: (oid, body) => d(client.post(`/orgs/${oid}/item-types`, body)),
  renameItemType: (tid, label, prefix) => d(client.put(`/item-types/${tid}`,
    prefix === undefined ? { label } : { label, prefix })),
  deleteItemType: (tid) => d(client.delete(`/item-types/${tid}`)),
  setProjectItemTypes: (pid, typeIds) => d(client.put(`/projects/${pid}/item-types`, { type_ids: typeIds })),
  addField: (tid, body) => d(client.post(`/item-types/${tid}/fields`, body)),
  updateField: (tid, fid, body) => d(client.put(`/item-types/${tid}/fields/${fid}`, body)),
  deleteField: (tid, fid) => d(client.delete(`/item-types/${tid}/fields/${fid}`)),
  // Prefix re-stamp (#174 residual gap): preview the affected count, then confirm.
  restampPreview: (tid) => d(client.get(`/item-types/${tid}/restamp-preview`)),
  // #388: optional {offset, limit} process one batch and return a cursor
  // ({done, total, complete}) so the admin UI can drive a determinate progress
  // bar; omit both to re-stamp the whole type in one call (original behaviour).
  /**
   * #1085: `mode` is 'keep_number' (the server default: REQ-0959 becomes
   * CR-0959) or 'mint_new' (each item gets a fresh number).
   * @param {string} tid
   * @param {{ offset?: number, limit?: number, mode?: string }} [batch]
   */
  restampItemType: (tid, { offset, limit, mode } = {}) => d(client.post(
    `/item-types/${tid}/restamp`, null,
    (offset === undefined && limit === undefined && mode === undefined)
      ? undefined : { params: { offset, limit, mode } })),

  // roles / capabilities
  roles: (oid) => d(client.get('/roles', { params: { org_id: oid } })),
  orgCaps: (oid) => d(client.get(`/orgs/${oid}/capabilities`)),
  setOrgCap: (oid, key, enabled) => d(client.put(`/orgs/${oid}/capabilities/${key}`, { enabled })),

  // user settings / keys / mcp
  settings: () => d(client.get('/me/settings')),
  saveSettings: (body) => d(client.put('/me/settings', body)),
  providerKeys: () => d(client.get('/me/provider-keys')),
  addProviderKey: (body) => d(client.post('/me/provider-keys', body)),
  delProviderKey: (id) => d(client.delete(`/me/provider-keys/${id}`)),
  // org provider keys (#210): the org's own LLM credential, admin-managed
  orgProviderKeys: (oid) => d(client.get(`/orgs/${oid}/provider-keys`)),
  setOrgProviderKey: (oid, body) => d(client.post(`/orgs/${oid}/provider-keys`, body)),
  delOrgProviderKey: (oid, id) => d(client.delete(`/orgs/${oid}/provider-keys/${id}`)),
  mcpTokens: () => d(client.get('/me/mcp-tokens')),
  createMcpToken: (oid, body) => d(client.post(`/orgs/${oid}/me/mcp-tokens`, body)),
  revokeMcpToken: (id) => d(client.delete(`/me/mcp-tokens/${id}`)),
  // #519 item 4: revoke every one of the caller's own tokens scoped to a project.
  revokeMcpTokensForProject: (oid, projectId) => d(client.delete(`/orgs/${oid}/me/mcp-tokens`, { params: { project_id: projectId } })),

  // super-admin / platform console
  adminUsers: (status) => d(client.get('/admin/users', { params: { status } })),
  approveUser: (uid) => d(client.post(`/admin/users/${uid}/approve`)),
  rejectUser: (uid) => d(client.post(`/admin/users/${uid}/reject`)),
  allowlist: () => d(client.get('/admin/allowlist')),
  addAllowlist: (email) => d(client.post('/admin/allowlist', { email })),
  delAllowlist: (email) => d(client.delete(`/admin/allowlist/${encodeURIComponent(email)}`)),
  adminOrgs: () => d(client.get('/admin/orgs')),
  setPlatformCap: (oid, key, allowed) => d(client.put(`/admin/orgs/${oid}/capabilities/${key}`, { allowed })),
  audit: (oid) => d(client.get('/admin/audit', { params: { org_id: oid } })),
  newsletter: () => d(client.get('/admin/newsletter')),
  // embedder selection (platform-wide)
  getEmbedding: () => d(client.get('/admin/embedding')),
  setEmbedding: (embedder) => d(client.put('/admin/embedding', { embedder })),
  // process memory snapshot (#376): RSS + gc + tracemalloc top-sites. `trace`
  // (true|false|undefined) starts/stops allocation tracing; `top` caps the sites.
  /** @param {{ trace?: boolean, top?: number, trim?: boolean }} [opts] */
  getMemory: ({ trace, top, trim } = {}) => d(client.get('/admin/memory', {
    params: {
      ...(trace === undefined ? {} : { trace }),
      ...(top ? { top } : {}),
      ...(trim ? { trim: true } : {}),
    },
  })),
  // error tracking (#1020 finding 3): is Sentry actually reporting from this
  // service, and prove it. `status` never returns the DSN — presence only.
  getErrorTracking: () => d(client.get('/admin/error-tracking')),
  testErrorTracking: () => d(client.post('/admin/error-tracking/test')),
  // orphan items (#175): platform-wide items not attached to any project
  adminOrphanItems: () => d(client.get('/admin/orphan-items')),
  assignOrphanItems: (itemIds, projectId) => d(client.post('/admin/orphan-items/assign',
    { item_ids: itemIds, project_id: projectId })),
  deleteOrphanItems: (itemIds) => d(client.post('/admin/orphan-items/delete', { item_ids: itemIds })),
};

export default iamApi;
